import React from "react";
import { useSelector } from "react-redux";
import { ChatBubbleOutline, Clear, ContactSupport, InfoOutlined, Place, Timeline } from "@mui/icons-material";
import { Skeleton, Tooltip } from "@mui/material";

import { CountsByCategory, FeatureTotalsSummary, listCategoryCounts } from "fond/map/featureTotalsSummary";
import { LegendButton } from "fond/map/Legend/Legend.styles";
import { clearSelection, selectSelectionState } from "fond/redux/selection";
import { PolygonIcon } from "fond/svg_icons";
import { Currency } from "fond/types/currency";
import { formatLength } from "fond/utils";
import { formatCurrency } from "fond/utils/currency";
import { useAppDispatch } from "fond/utils/hooks";

import {
  CategoryCounter,
  SummaryContainer,
  SummaryHeader,
  SummaryTile,
  SummaryTiles,
  SummaryTitle,
  TileCaption,
  TileValue,
} from "./SelectionSummary.styles";

const CATEGORY_ICONS: Record<keyof CountsByCategory, React.ReactElement> = {
  points: <Place />,
  lines: <Timeline />,
  polygons: <PolygonIcon />,
  comments: <ChatBubbleOutline />,
  other: <ContactSupport />,
};

interface SelectionSummaryProps {
  /** Aggregate selection totals; undefined while totals are being fetched. */
  summary?: FeatureTotalsSummary;
  systemOfMeasurement: "imperial" | "metric";
  /** Currency the cost is displayed in; defaults to USD. */
  currency?: Currency;
  /** Whether to show the cost tile; only meaningful where every design layer is costed. */
  showCost?: boolean;
}

/**
 * Legend panel summarising the features within the current selection polygon.
 */
const SelectionSummary: React.FC<SelectionSummaryProps> = ({ summary, systemOfMeasurement, currency, showCost = false }) => {
  const dispatch = useAppDispatch();
  const selection = useSelector(selectSelectionState);

  if (selection.status !== "selected") return null;

  const categoryCounts = summary ? listCategoryCounts(summary.countsByCategory) : [];

  return (
    <SummaryContainer data-testid="selection-summary">
      <SummaryHeader>
        <SummaryTitle>Selected features</SummaryTitle>
        <Tooltip title="Clear selection" PopperProps={{ disablePortal: true }}>
          <LegendButton variant="outlined" size="small" startIcon={<Clear />} onClick={() => dispatch(clearSelection())}>
            Clear
          </LegendButton>
        </Tooltip>
      </SummaryHeader>
      <SummaryTiles>
        <SummaryTile>
          <TileCaption>Features</TileCaption>
          <TileValue>
            {!summary && <Skeleton width={48} data-testid="summary-value-skeleton" />}
            {summary && categoryCounts.length === 0 && "0"}
            {categoryCounts.map(({ category, count, label }) => (
              <Tooltip key={category} title={label} PopperProps={{ disablePortal: true }}>
                <CategoryCounter aria-label={label}>
                  {count.toLocaleString()}
                  {CATEGORY_ICONS[category]}
                </CategoryCounter>
              </Tooltip>
            ))}
          </TileValue>
        </SummaryTile>
        <SummaryTile>
          <TileCaption>
            Line length
            <Tooltip
              title="Total length of all lines within the selection. Lines that cross the selection boundary contribute their full length."
              PopperProps={{ disablePortal: true }}
              slotProps={{ tooltip: { sx: { textTransform: "none" } } }}
            >
              <InfoOutlined aria-label="Line length help" />
            </Tooltip>
          </TileCaption>
          <TileValue data-testid="line-length-value">
            {summary ? (
              formatLength(summary.length, { from: "metric", to: systemOfMeasurement, decimalPlaces: 0 })
            ) : (
              <Skeleton width={48} data-testid="summary-value-skeleton" />
            )}
          </TileValue>
        </SummaryTile>
        {showCost && summary?.cost != null && (
          <SummaryTile>
            <TileCaption>
              Cost
              <Tooltip
                title="Total cost of all features within the selection."
                PopperProps={{ disablePortal: true }}
                slotProps={{ tooltip: { sx: { textTransform: "none" } } }}
              >
                <InfoOutlined aria-label="Cost help" />
              </Tooltip>
            </TileCaption>
            <TileValue data-testid="cost-value">{formatCurrency(summary.cost, { currency })}</TileValue>
          </SummaryTile>
        )}
      </SummaryTiles>
    </SummaryContainer>
  );
};

export default SelectionSummary;
